import { useEffect } from "react";
import { LANGUAGES, DEFAULT_LANG } from "@/i18n/languages";

// Canonical production domain for SEO metadata. Env-overridable, but always
// falls back to the production domain on purpose — canonical/OG/JSON-LD URLs
// must never point at preview origins.
const SITE = process.env.REACT_APP_SITE_URL || "https://doublerainbowsuites.com";
const MANAGED_ID = "drs-managed-seo";

// Build the alternate URL for a given language. English (default) keeps the
// clean canonical URL; other languages append ?lang=<code>, which the runtime
// i18n engine reads on load to render that language.
const altUrl = (base, code) =>
  code === DEFAULT_LANG ? base : `${base}?lang=${code}`;

/**
 * Lightweight, React 19-friendly head manager.
 * Injects/updates <title>, meta description, canonical link, Open Graph tags
 * and one or more JSON-LD blocks — then cleans them up on unmount so each
 * route ships its own AI-search-ready metadata.
 */
export default function Seo({ title, description, canonical, image, jsonLd, keywords, noindex }) {
  useEffect(() => {
    const created = [];

    // When a ?lang= override is present, the page self-canonicalizes to that
    // language variant so the hreflang cluster is reciprocal and valid.
    let langParam = null;
    try {
      langParam = new URLSearchParams(window.location.search).get("lang");
    } catch (e) {
      /* ignore */
    }
    const selfCanonical =
      canonical && langParam && langParam !== DEFAULT_LANG
        ? altUrl(canonical, langParam)
        : canonical;

    // Title
    const prevTitle = document.title;
    if (title) document.title = title;

    const setMeta = (attr, key, content) => {
      if (!content) return;
      let el = document.head.querySelector(`meta[${attr}="${key}"]`);
      if (!el) {
        el = document.createElement("meta");
        el.setAttribute(attr, key);
        el.setAttribute(MANAGED_ID, "1");
        document.head.appendChild(el);
        created.push(el);
      }
      const prev = el.getAttribute("content");
      el.setAttribute("content", content);
      if (!created.includes(el)) el.dataset.prevContent = prev ?? "";
    };

    setMeta("name", "description", description);
    setMeta("name", "keywords", keywords);
    if (noindex) setMeta("name", "robots", "noindex, nofollow");
    setMeta("property", "og:title", title);
    setMeta("property", "og:description", description);
    setMeta("property", "og:url", selfCanonical);
    setMeta("property", "og:image", image);
    setMeta("name", "twitter:title", title);
    setMeta("name", "twitter:description", description);
    setMeta("name", "twitter:image", image);

    // Canonical
    if (canonical) {
      let link = document.head.querySelector('link[rel="canonical"]');
      if (!link) {
        link = document.createElement("link");
        link.setAttribute("rel", "canonical");
        document.head.appendChild(link);
        created.push(link);
      }
      link.dataset.prevHref = link.getAttribute("href") ?? "";
      link.setAttribute("href", selfCanonical);
    }

    // hreflang alternates — one per supported language + an x-default pointing
    // to English. Added on every page (each page renders <Seo>) so search/AI
    // engines can serve the right language version to international visitors.
    if (canonical) {
      const addAlt = (hreflang, href) => {
        const l = document.createElement("link");
        l.setAttribute("rel", "alternate");
        l.setAttribute("hreflang", hreflang);
        l.setAttribute("href", href);
        l.setAttribute(MANAGED_ID, "1");
        document.head.appendChild(l);
        created.push(l);
      };
      LANGUAGES.forEach(({ code }) => addAlt(code, altUrl(canonical, code)));
      addAlt("x-default", altUrl(canonical, DEFAULT_LANG));
    }

    // JSON-LD blocks
    const blocks = Array.isArray(jsonLd) ? jsonLd : jsonLd ? [jsonLd] : [];
    const ldNodes = blocks.map((data) => {
      const s = document.createElement("script");
      s.type = "application/ld+json";
      s.setAttribute(MANAGED_ID, "1");
      s.text = JSON.stringify(data);
      document.head.appendChild(s);
      return s;
    });

    return () => {
      document.title = prevTitle;
      ldNodes.forEach((n) => n.remove());
      created.forEach((n) => n.remove());
      // restore mutated (pre-existing) meta/canonical
      document.head.querySelectorAll("meta[data-prev-content]").forEach((el) => {
        el.setAttribute("content", el.dataset.prevContent);
        delete el.dataset.prevContent;
      });
      const link = document.head.querySelector("link[rel='canonical'][data-prev-href]");
      if (link) {
        link.setAttribute("href", link.dataset.prevHref);
        delete link.dataset.prevHref;
      }
    };
  }, [title, description, canonical, image, jsonLd]);

  return null;
}

export { SITE };
